import { reactive, watch, ref, nextTick } from 'vue';
import axios from 'axios';

// Cargar el carrito desde localStorage
const loadCartFromLocalStorage = () => {
  const cartData = localStorage.getItem('cart');
  return cartData ? JSON.parse(cartData) : [];
};

// --- SINGLETON STATE ---
const state = reactive({
  cart: loadCartFromLocalStorage(),
});

// Sincronizar con localStorage
watch(
  () => state.cart,
  (newCart) => {
    localStorage.setItem('cart', JSON.stringify(newCart));
  },
  { deep: true }
);

// ----------------------------------------------------------
// NORMAL addToCart
// ----------------------------------------------------------
const addToCart = (product) => {
  const existing = state.cart.find(
    item => item.sku === product.sku && item.id === product.id
  );

  // límite de promociones
  if (existing && (existing.id_promocion || existing.promoUnico || existing.precio_con_descuento)) {
    if (existing.quantity >= 5) {
      alert('Máximo 5 unidades por producto en promoción.');
      return;
    }
    existing.quantity += 1;
    return;
  }

  // producto con descuento
  if (product.precio_con_descuento) {
    state.cart.push({
      ...product,
      price: product.precio_con_descuento,
      quantity: 1,
      promoUnico: true
    });
  }

  // producto normal existente → aumentar hasta 5
  else if (existing) {
    if (existing.quantity < 5) {
      existing.quantity += 1;
    } else {
      alert('Solo puedes agregar hasta 5 unidades de este producto.');
    }
  }

  // producto nuevo
  else {
    state.cart.push({ ...product, price: product.price, quantity: 1 });
  }

  if (navigator.vibrate) navigator.vibrate(50);
};

// ----------------------------------------------------------
// RESETEAR STATE
// ----------------------------------------------------------
const lastUpdated = ref(Date.now());

const resetState = async () => {
  state.cart = [];
  lastUpdated.value = Date.now();
  localStorage.removeItem('cart');
  localStorage.removeItem('checkoutTotal');
  await nextTick();
};

// ----------------------------------------------------------
// PROMOCIONES addToCart2
// ----------------------------------------------------------
const addToCart2 = async (product) => {
  try {
    const response = await axios.get(`https://mvexpress.mx/api/promotion/${product.id}`);
    const promoDetails = response.data[0];

    if (!promoDetails) return alert("No se encontró información de la promoción.");

    const existingProduct = state.cart.find(
      item => item.sku === promoDetails.sku && item.id === promoDetails.id
    );

    if (existingProduct) {
      if (existingProduct.quantity < 5) {
        existingProduct.quantity += 1;
      } else {
        alert("Solo puedes agregar hasta 5 unidades de este producto.");
      }
    } else {
      state.cart.push({
        ...promoDetails,
        quantity: 1,
        price: parseFloat(promoDetails.promo_price || 0),
        promoUnico: true,
      });
    }
  } catch (error) {
    console.error("Error al obtener detalles de la promoción:", error);
    alert("No se pudo agregar esta promoción al carrito.");
  }
};

// ----------------------------------------------------------
// 🔥🔥🔥 addGiftToCart (PROMOCIÓN CON REGALO BLOQUEADA) 🔥🔥🔥
// ----------------------------------------------------------
const addGiftToCart = async (item) => {
  try {
    console.log("🔹 Agregando promoción al carrito:", item);

    // 1️⃣ Obtener datos de la promoción
    const { data: promo } = await axios.get(`https://mvexpress.mx/api/promotion2/${item.id}`);
    if (!promo) return alert("No se encontraron detalles de la promoción.");

    const cantidad = promo.cantidad || 1;  // principal debe quedar fijo en esta cantidad
    const comboId = `combo-${promo.id}`;

    // 2️⃣ Cargar productos del combo
    const { data: mainData } = await axios.get(`https://mvexpress.mx/api/get-sku/${promo.sku_producto}`);
    const mainProduct = mainData[0];

    const { data: giftData } = await axios.get(`https://mvexpress.mx/api/get-sku/${promo.sku_regalo}`);
    const giftProduct = giftData[0];

    if (!mainProduct || !giftProduct) {
      return alert("No se pudieron obtener los productos.");
    }

    // 3️⃣ Buscar existentes
    const existingMain = state.cart.find(
      p => p.sku === mainProduct.sku && p.comboId === comboId && p.isMain
    );

    const existingGift = state.cart.find(
      p => p.sku === giftProduct.sku && p.comboId === comboId && p.isGift
    );

    // ------------------------------
    // 🔹 4️⃣ PRODUCTO PRINCIPAL (NO SE AUMENTA)
    // ------------------------------
    if (!existingMain) {
      // Se agrega solo una vez con la cantidad de la promo (5,10,...)
      state.cart.push({
        ...mainProduct,
        quantity: cantidad,
        comboId,
        isMain: true,
        promoUnico: true,
        price: parseFloat(mainProduct.precio_con_descuento) || parseFloat(mainProduct.price) || 0
      });
    }
    // ❗ Si ya existe, NO aumentar más
    else {
      existingMain.quantity = cantidad; // siempre fijo
    }

    // ------------------------------
    // 🔹 5️⃣ PRODUCTO REGALO (SUMA 1 POR CLICK)
    // ------------------------------
    if (existingGift) {
      existingGift.quantity += 1; // ahora sí suma correcto
    } else {
      state.cart.push({
        ...giftProduct,
        quantity: 1,
        comboId,
        isGift: true,
        price: 0,
        nombre: `${giftProduct.nombre} 🎁`
      });
    }

    // Guardar cambios
    localStorage.setItem("cart", JSON.stringify(state.cart));

    alert(`Promo "${promo.nombre}" agregada correctamente.`);

  } catch (error) {
    console.error("❌ Error al agregar promoción:", error);
    alert("No se pudo agregar la promoción.");
  }
};

// ----------------------------------------------------------
// ELIMINAR
// ----------------------------------------------------------
const removeFromCart = (index) => {
  state.cart.splice(index, 1);
};

const removeComboFromCart = (comboId) => {
  state.cart = state.cart.filter(p => p.comboId !== comboId);
};

// ----------------------------------------------------------
// BLOQUEAR + / - PARA PROMOCIONES 🔒
// ----------------------------------------------------------
const increaseQuantity = (item) => {
  if (item.isLocked) {
    alert("Esta promoción no permite modificar cantidades.");
    return;
  }

  const existingProduct = state.cart.find(
    (product) => product.sku === item.sku && product.id === item.id
  );

  if (!existingProduct) return;

  if (existingProduct.quantity >= 40) {
    alert('Solo puedes agregar hasta 40 unidades.');
    return;
  }

  existingProduct.quantity++;
};

const decreaseQuantity = (item) => {
  if (item.isLocked) {
    alert("Esta promoción no permite modificar cantidades.");
    return;
  }

  const index = state.cart.findIndex(
    (product) => product.sku === item.sku && product.id === item.id
  );

  if (index === -1) return;

  if (state.cart[index].quantity > 1) {
    state.cart[index].quantity--;
  } else {
    state.cart.splice(index, 1);
  }
};

// ----------------------------------------------------------
const getCartTotal = () => {
  return state.cart.reduce((total, item) => {
    if (item.sku && item.sku.startsWith('PP')) return total;
    const price = item.precio_con_descuento || item.price;
    return total + price * item.quantity;
  }, 0).toFixed(2);
};

const getTotalCajas = () => {
  return state.cart.reduce((total, item) => total + item.quantity, 0);
};

const getProductQuantity = (product) => {
  const existing = state.cart.find(
    (item) => item.sku === product.sku && item.id === product.id
  );
  return existing ? existing.quantity : 0;
};

// ----------------------------------------------------------
const checkout = () => {
  const total = state.cart.reduce((acc, item) => {
    if (item.sku?.startsWith('PP')) return acc;
    const price = item.precio_con_descuento || item.price;
    return acc + price * item.quantity;
  }, 0);

  const rounded = parseFloat(total.toFixed(2));
  localStorage.setItem('checkoutTotal', rounded);
  alert(`Total a pagar: $${rounded}`);
  return rounded;
};

// ----------------------------------------------------------
export function createCartStore() {
  return {
    state,
    resetState,
    lastUpdated,
    loadCartFromLocalStorage,
    addToCart,
    addToCart2,
    addGiftToCart,
    removeFromCart,
    removeComboFromCart,
    clearCart: resetState,
    getCartTotal,
    increaseQuantity,
    decreaseQuantity,
    getTotalCajas,
    getProductQuantity,
    checkout,
  };
}
